import type { AuthenticationOutcome } from '@ayumia/contracts';
import { API_PREFIX, ApiError, csrfToken, forgetCsrfToken, parseJsonBody, rememberCsrfToken, retryAfterOf, type Fetch } from './client';

/**
 * Client des routes de connexion (ADR-091). Le jeton de session ne passe jamais par le script de
 * la page : le serveur le pose dans un cookie `HttpOnly`, le navigateur le renvoie seul. La page ne
 * garde que le jeton anti-CSRF, reçu dans un en-tête de réponse, qu'elle recopie dans l'en-tête des
 * requêtes qui écrivent.
 */
export class AuthClient {
  private readonly fetchImpl: Fetch;

  constructor(options: { fetch?: Fetch; baseUrl?: string } = {}) {
    this.fetchImpl = options.fetch ?? ((input, init) => fetch(input, init));
    this.baseUrl = options.baseUrl ?? '';
  }

  private readonly baseUrl: string;

  private async request(method: 'GET' | 'POST', path: string, body?: Record<string, unknown>): Promise<AuthenticationOutcome> {
    const headers: Record<string, string> = { accept: 'application/json' };
    const csrf = csrfToken();
    if (method === 'POST' && csrf !== null) headers['x-csrf-token'] = csrf;
    if (body !== undefined) headers['content-type'] = 'application/json';
    let response: Response;
    try {
      response = await this.fetchImpl(`${this.baseUrl}${API_PREFIX}${path}`, { method, headers, credentials: 'same-origin', ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
    } catch {
      throw new ApiError('DEPENDENCY_FAILURE', 'NETWORK_UNAVAILABLE', 'Le serveur est injoignable.', 0);
    }
    rememberCsrfToken(response);
    const text = await response.text();
    const parsed: unknown = parseJsonBody(text);
    if (!response.ok) {
      const error = (parsed ?? {}) as { category?: string; code?: string; message?: string; field_errors?: ApiError['fieldErrors'] };
      throw new ApiError(error.category ?? 'INTERNAL_ERROR', error.code ?? `HTTP_${response.status}`, error.message ?? '', response.status, error.field_errors ?? [], retryAfterOf(response));
    }
    return parsed as AuthenticationOutcome;
  }

  /**
   * Habillage public de la page de connexion (ADR-092 §7) : lu sans session, par ce client autorisé
   * à parler à l'API (aucun autre module du front n'appelle le réseau). Corps rendu brut :
   * auth/sign-in-presentation le valide et retombe sur le défaut.
   */
  async signInPresentation(): Promise<unknown> {
    return (await this.request('GET', '/public/sign-in-presentation')) as unknown;
  }

  me(): Promise<AuthenticationOutcome> {
    return this.request('GET', '/auth/me');
  }

  login(email: string, password: string): Promise<AuthenticationOutcome> {
    return this.request('POST', '/auth/login', { email, password });
  }

  verifyTotp(challenge: string, code: string): Promise<AuthenticationOutcome> {
    return this.request('POST', '/auth/totp', { challenge, code });
  }

  startEnrollment(input: { challenge: string } | { current_password: string }): Promise<AuthenticationOutcome> {
    return this.request('POST', '/auth/totp/enrollment', input);
  }

  confirmEnrollment(code: string, challenge?: string): Promise<AuthenticationOutcome> {
    return this.request('POST', '/auth/totp/confirm', challenge === undefined ? { code } : { code, challenge });
  }

  stepUp(code: string): Promise<AuthenticationOutcome> {
    return this.request('POST', '/auth/step-up', { code });
  }

  changePassword(current_password: string, new_password: string): Promise<AuthenticationOutcome> {
    return this.request('POST', '/auth/password', { current_password, new_password });
  }

  requestReset(email: string): Promise<AuthenticationOutcome> {
    return this.request('POST', '/auth/password-reset/request', { email });
  }

  confirmReset(token: string, password: string): Promise<AuthenticationOutcome> {
    return this.request('POST', '/auth/password-reset/confirm', { token, password });
  }

  async logout(): Promise<AuthenticationOutcome> {
    try {
      return await this.request('POST', '/auth/logout');
    } finally {
      forgetCsrfToken();
    }
  }
}
